import React, { useEffect, useRef, useState } from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useScopedPath } from '../ScopedLink';
import {
  useStartFireDrillMutation,
  useGetClassesQuery,
  useGetLocationStaffQuery,
} from '../../store/api/localApi';
import { FireDrill, FireDrillScope, FireDrillType } from '../../types';
import { useToast } from '../Toast';
import { useAuth } from '../../hooks/useAuth';

export interface StartDrillModalProps {
  open: boolean;
  onClose: () => void;
  locationId: string;
  /** Recent drills used to compute the same-exit soft warning.
   *  Pass from parent — typically the first ~10 drills from useListFireDrillsQuery. */
  recentDrills?: FireDrill[];
}

const StartDrillModal: React.FC<StartDrillModalProps> = ({
  open,
  onClose,
  locationId,
  recentDrills,
}) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const scopedPath = useScopedPath();
  const { user, activeRole } = useAuth();
  const [startFireDrill, { isLoading }] = useStartFireDrillMutation();

  // --- Form state ---
  const [mode, setMode] = useState<'LIVE' | 'PAST'>('LIVE');
  const [scope, setScope] = useState<FireDrillScope>(FireDrillScope.LOCATION);
  const [type, setType] = useState<FireDrillType>(FireDrillType.ANNOUNCED);
  const [classId, setClassId] = useState('');
  const [assemblyPoint, setAssemblyPoint] = useState('');
  const [exitRoute, setExitRoute] = useState('');
  const [scenario, setScenario] = useState('');
  const [selectedStaffIds, setSelectedStaffIds] = useState<Set<string>>(new Set());
  const [startedAt, setStartedAt] = useState('');
  const [completedAt, setCompletedAt] = useState('');

  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  // --- Data fetching ---
  const { data: classes } = useGetClassesQuery(locationId, {
    skip: !open || scope !== FireDrillScope.CLASS,
  });

  // Narrow STAFF-readable endpoint — returns just id+name+role per staff
  // member. STAFF callers can't see the broader /members endpoint that
  // includes email + pending-invite state, so we use this purpose-built
  // shape instead.
  const { data: staffData } = useGetLocationStaffQuery({ locationId }, { skip: !open });

  // The person running the drill (staff OR admin) is always present and
  // selectable — include them even if they aren't in the location's staff
  // list (e.g. an org/location admin, or a location with no other staff).
  const staffMembers = (() => {
    const list = (staffData?.staff || []).map((s) => ({
      userId: s.id,
      firstName: s.firstName,
      lastName: s.lastName,
      role: s.role,
      isSelf: s.id === user?.id,
    }));
    if (user && !list.some((m) => m.userId === user.id)) {
      list.unshift({
        userId: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        role: activeRole,
        isSelf: true,
      });
    }
    return list;
  })();

  // --- Soft warning: same exit route as last 2 drills ---
  const lastTwoExits = recentDrills?.slice(0, 2).map((d) => d.exitRoute) ?? [];
  const showExitWarning =
    lastTwoExits.length === 2 && lastTwoExits.every((e) => e === exitRoute) && exitRoute.length > 0;

  // --- Reset form on open ---
  useEffect(() => {
    if (open) {
      setMode('LIVE');
      setScope(FireDrillScope.LOCATION);
      setType(FireDrillType.ANNOUNCED);
      setClassId('');
      setAssemblyPoint('');
      setExitRoute('');
      setScenario('');
      // Live drills default to the runner being present (they're inherently
      // there). Past logs start empty — the recorder may not have attended.
      setSelectedStaffIds(user ? new Set([user.id]) : new Set());
      setStartedAt('');
      setCompletedAt('');
      closeBtnRef.current?.focus();
    }
  }, [open, user]);

  // Switching mode adjusts the default self-selection: LIVE pre-selects the
  // runner, PAST clears it (free selection of who was actually present).
  useEffect(() => {
    if (!open) return;
    if (mode === 'LIVE' && user) {
      setSelectedStaffIds((prev) => (prev.has(user.id) ? prev : new Set([...prev, user.id])));
    } else if (mode === 'PAST' && user) {
      setSelectedStaffIds((prev) => {
        if (!prev.has(user.id)) return prev;
        const next = new Set(prev);
        next.delete(user.id);
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // --- Esc key handler ---
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const toggleStaff = (userId: string) => {
    setSelectedStaffIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const canSubmit =
    assemblyPoint.trim().length > 0 &&
    exitRoute.trim().length > 0 &&
    // LIVE: the runner is auto-included server-side, so no selection is
    // required. PAST: the recorder must record at least one person present.
    (mode === 'LIVE' || selectedStaffIds.size > 0) &&
    (scope !== FireDrillScope.CLASS || classId !== '') &&
    (mode !== 'PAST' || startedAt !== '');

  const handleSubmit = async () => {
    if (!canSubmit || isLoading) return;
    try {
      const res = await startFireDrill({
        locationId,
        type,
        scope,
        classId: scope === FireDrillScope.CLASS ? classId : undefined,
        assemblyPoint: assemblyPoint.trim(),
        exitRoute: exitRoute.trim(),
        scenario: scenario.trim() || undefined,
        isLiveRecording: mode === 'LIVE',
        // datetime-local inputs return "YYYY-MM-DDTHH:mm" (no seconds, no
        // timezone). The backend validates with zod .datetime() which
        // demands a full ISO-8601 string. Normalize before sending.
        startedAt: mode === 'PAST' && startedAt ? new Date(startedAt).toISOString() : undefined,
        completedAt:
          mode === 'PAST' && completedAt ? new Date(completedAt).toISOString() : undefined,
        staffIds: Array.from(selectedStaffIds),
      }).unwrap();
      showSuccess(mode === 'LIVE' ? 'Drill started — recording live.' : 'Drill recorded.');
      onClose();
      const base = scopedPath(`/fire-drills/${res.drill.id}`);
      navigate(mode === 'LIVE' ? `${base}/live` : base);
    } catch (err) {
      showError((err as any)?.data?.error || 'Failed to start drill');
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      data-testid="start-drill-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="start-drill-title"
    >
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75"
          onClick={onClose}
          aria-hidden="true"
        />

        <div className="relative z-10 bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-5">
            <h3 id="start-drill-title" className="text-lg font-semibold text-gray-900">
              Start fire drill
            </h3>
            <button
              ref={closeBtnRef}
              type="button"
              onClick={onClose}
              data-testid="start-drill-cancel"
              className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Mode */}
          <fieldset className="mb-4">
            <legend className="text-sm font-medium text-gray-700 mb-1">Mode</legend>
            <label className="inline-flex items-center mr-4">
              <input
                type="radio"
                data-testid="start-drill-mode-live"
                checked={mode === 'LIVE'}
                onChange={() => setMode('LIVE')}
                className="mr-1.5"
              />
              <span className="text-sm">Live</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                data-testid="start-drill-mode-past"
                checked={mode === 'PAST'}
                onChange={() => setMode('PAST')}
                className="mr-1.5"
              />
              <span className="text-sm">After-the-fact</span>
            </label>
          </fieldset>

          {/* Scope */}
          <fieldset className="mb-4">
            <legend className="text-sm font-medium text-gray-700 mb-1">Scope</legend>
            <label className="inline-flex items-center mr-4">
              <input
                type="radio"
                data-testid="start-drill-scope-location"
                checked={scope === FireDrillScope.LOCATION}
                onChange={() => setScope(FireDrillScope.LOCATION)}
                className="mr-1.5"
              />
              <span className="text-sm">Whole location</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                data-testid="start-drill-scope-class"
                checked={scope === FireDrillScope.CLASS}
                onChange={() => setScope(FireDrillScope.CLASS)}
                className="mr-1.5"
              />
              <span className="text-sm">Single class</span>
            </label>
          </fieldset>

          {scope === FireDrillScope.CLASS && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
              <select
                data-testid="start-drill-class-picker"
                value={classId}
                onChange={(e) => setClassId(e.target.value)}
                className="block w-full rounded-md border-gray-300 text-sm"
              >
                <option value="">Select a class…</option>
                {(classes || []).map((cls) => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Type */}
          <fieldset className="mb-4">
            <legend className="text-sm font-medium text-gray-700 mb-1">Type</legend>
            <label className="inline-flex items-center mr-4">
              <input
                type="radio"
                data-testid="start-drill-type-announced"
                checked={type === FireDrillType.ANNOUNCED}
                onChange={() => setType(FireDrillType.ANNOUNCED)}
                className="mr-1.5"
              />
              <span className="text-sm">Announced</span>
            </label>
            <label className="inline-flex items-center mr-4">
              <input
                type="radio"
                data-testid="start-drill-type-unannounced"
                checked={type === FireDrillType.UNANNOUNCED}
                onChange={() => setType(FireDrillType.UNANNOUNCED)}
                className="mr-1.5"
              />
              <span className="text-sm">Unannounced</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                data-testid="start-drill-type-real"
                checked={type === FireDrillType.EVAC_REAL}
                onChange={() => setType(FireDrillType.EVAC_REAL)}
                className="mr-1.5"
              />
              <span className="text-sm">Real evacuation</span>
            </label>
          </fieldset>

          {/* Exit route */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Exit route <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              data-testid="start-drill-exit-route"
              value={exitRoute}
              onChange={(e) => setExitRoute(e.target.value.slice(0, 120))}
              placeholder="e.g. Main corridor → rear fire door"
              className="block w-full rounded-md border-gray-300 text-sm"
            />
            {showExitWarning && (
              <div
                data-testid="start-drill-exit-warning"
                className="mt-1.5 flex items-start gap-1.5 text-amber-700 text-xs"
              >
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  This exit route was used in the last 2 drills. Consider varying routes for
                  compliance.
                </span>
              </div>
            )}
          </div>

          {/* Assembly point */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assembly point <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              data-testid="start-drill-assembly-point"
              value={assemblyPoint}
              onChange={(e) => setAssemblyPoint(e.target.value.slice(0, 120))}
              placeholder="e.g. Car park — east end"
              className="block w-full rounded-md border-gray-300 text-sm"
            />
          </div>

          {/* Scenario (optional) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scenario <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              data-testid="start-drill-scenario"
              value={scenario}
              onChange={(e) => setScenario(e.target.value.slice(0, 200))}
              placeholder="e.g. Kitchen fire — alarm triggered at 10:00"
              className="block w-full rounded-md border-gray-300 text-sm"
            />
          </div>

          {/* After-the-fact date pickers */}
          {mode === 'PAST' && (
            <div className="mb-4 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Started at <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  data-testid="start-drill-started-at"
                  value={startedAt}
                  onChange={(e) => setStartedAt(e.target.value)}
                  className="block w-full rounded-md border-gray-300 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Completed at <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="datetime-local"
                  data-testid="start-drill-completed-at"
                  value={completedAt}
                  onChange={(e) => setCompletedAt(e.target.value)}
                  className="block w-full rounded-md border-gray-300 text-sm"
                />
              </div>
            </div>
          )}

          {/* Staff multi-select */}
          <fieldset className="mb-5">
            <legend className="text-sm font-medium text-gray-700 mb-1">
              Staff present {mode === 'PAST' && <span className="text-red-500">*</span>}
            </legend>
            <p className="text-xs text-gray-400 mb-1">
              {mode === 'LIVE'
                ? "You're included as present. Add any other staff here."
                : 'Select everyone who was present (at least one).'}
            </p>
            {staffMembers.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No staff found at this location.</p>
            ) : (
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
                {staffMembers.map((m: any) => (
                  <label
                    key={m.userId}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      data-testid={`start-drill-staff-${m.userId}`}
                      checked={selectedStaffIds.has(m.userId)}
                      onChange={() => toggleStaff(m.userId)}
                    />
                    <span className="text-sm text-gray-800">
                      {m.firstName} {m.lastName}
                      {m.isSelf && <span className="text-gray-400"> (you)</span>}
                    </span>
                    <span className="ml-auto text-xs text-gray-400">{m.role}</span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="start-drill-submit"
              onClick={handleSubmit}
              disabled={!canSubmit || isLoading}
              className="px-3 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Starting…' : mode === 'LIVE' ? 'Start drill' : 'Record drill'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartDrillModal;
